import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, Clock } from 'lucide-react';
import Header from '../Header';
import Footer from '../Footer';
import SEOHead from './SEOHead';
import SEOFloatingCTA from './SEOFloatingCTA';
import SEORelatedLinks from './SEORelatedLinks';
import { fadeInUp } from '../../lib/animations';
import { Button } from '../ui/button';

const SEOPageLayout = ({ 
  title, 
  metaTitle, 
  metaDescription, 
  url, 
  readingTime, 
  subtitle,
  children, 
  relatedLinks 
}) => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#09090b]" data-testid="seo-page">
      <SEOHead title={metaTitle} description={metaDescription} url={url} />

      <Header />

      {/* Hero */}
      <section className="pt-32 md:pt-40 pb-12 px-6 md:px-12" data-testid="seo-hero">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Link 
              to="/" 
              className="inline-flex items-center gap-2 text-[#71717a] hover:text-[#3b82f6] text-sm mb-8 transition-colors"
              data-testid="seo-breadcrumb-home"
            >
              <ArrowLeft className="w-4 h-4" />
              Retour à l'accueil
            </Link>

            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#f5f5f5] tracking-tight leading-tight mb-6" data-testid="seo-page-title">
              {title}
            </h1>

            {subtitle && (
              <p className="text-lg text-[#a1a1aa] mb-4">{subtitle}</p>
            )}

            {readingTime && (
              <div className="flex items-center gap-2 text-[#71717a] text-sm" data-testid="seo-reading-time">
                <Clock className="w-4 h-4" />
                Lecture : {readingTime} min
              </div>
            )}
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="pb-24 px-6 md:px-12" data-testid="seo-content">
        <div className="max-w-3xl mx-auto">
          <motion.div {...fadeInUp}>
            {children}
          </motion.div>

          {/* CTA Block */}
          <motion.div 
            {...fadeInUp} 
            className="mt-16 p-8 md:p-10 rounded-2xl border border-[#3b82f6]/20 bg-[#1a1a2e]/50 text-center"
            data-testid="seo-cta-block"
          >
            <p className="text-xl md:text-2xl font-semibold text-[#f5f5f5] mb-3">
              Cloneia crée un clone mémoire de chaque collaborateur.
            </p>
            <p className="text-[#a1a1aa] mb-6">
              15 minutes de démo. Zéro engagement.
            </p>
            <Link to="/">
              <Button 
                className="bg-[#3b82f6] hover:bg-[#2563eb] text-white px-8 py-3 rounded-full font-semibold transition-all"
                data-testid="seo-cta-button"
              >
                Demander une démo
              </Button>
            </Link>
          </motion.div>

          <SEORelatedLinks links={relatedLinks} />
        </div>
      </section>

      <SEOFloatingCTA />
      <Footer />
    </div>
  );
};

export default SEOPageLayout;
